import { useState } from "react";
import { Search, CheckCircle2, Truck, Circle } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TrackShipment = () => {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [miss, setMiss] = useState(false);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setMiss(false);
    setResult(null);
    try {
      const res = await fetch(`${API}/track/${encodeURIComponent(code.trim())}`);
      if (!res.ok) throw new Error("not found");
      setResult(await res.json());
    } catch {
      setMiss(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="track"
      data-testid="track-section"
      className="scroll-mt-20 py-24 sm:py-32 bg-[#161B22] border-y border-slate-800"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <SectionHead
            overline="Track Your Shipment"
            title="Where is my freight?"
            sub="Live GPS tracking integration is on the way. The console below is a working preview — enter the sample consignment code to see how tracking will read."
          />
          <Reveal delay={0.15} className="mt-10">
            <form onSubmit={lookup} className="flex flex-col sm:flex-row gap-3">
              <input
                data-testid="track-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter consignment code — try AG-88492"
                className="mono flex-1 bg-[#0D1117] border border-slate-700 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-amber-500 transition-colors duration-200"
              />
              <button
                type="submit"
                data-testid="track-submit-btn"
                disabled={loading}
                className="mono inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold bg-amber-500 text-[#0D1117] px-7 py-3.5 hover:bg-amber-400 disabled:opacity-60 transition-colors duration-200"
              >
                <Search className="w-4 h-4" /> {loading ? "Tracking…" : "Track"}
              </button>
            </form>
            <p className="mono mt-3 text-[10px] uppercase tracking-[0.15em] text-slate-600">
              Preview environment — sample code AG-88492
            </p>
            <p data-testid="track-powered-by" className="mono mt-2 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-amber-500/80 border border-amber-500/30 px-2.5 py-1">
              <span className="w-1.5 h-1.5 bg-amber-500" />
              Powered by Tvarit Control Tower System
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div
            data-testid="track-result"
            className="border border-slate-800 bg-[#0D1117] min-h-[280px] p-6 sm:p-8"
          >
            {!result && !miss && (
              <div className="h-full flex flex-col items-start justify-center gap-3 text-slate-600">
                <Truck className="w-8 h-8" />
                <p className="mono text-xs uppercase tracking-[0.2em]">
                  Awaiting consignment code
                </p>
              </div>
            )}

            {miss && (
              <div data-testid="track-not-found" className="h-full flex flex-col items-start justify-center gap-3">
                <p className="mono text-xs uppercase tracking-[0.2em] text-amber-500">
                  No record found
                </p>
                <p className="text-sm text-slate-400 leading-relaxed">
                  No consignment found for this code. For a live status on your shipment,
                  write to <a href="mailto:contact@agaman.co" className="text-amber-400 hover:text-amber-300 underline underline-offset-4">contact@agaman.co</a>.
                </p>
              </div>
            )}

            {result && (
              <div>
                <div className="flex flex-wrap items-center justify-between gap-3 pb-5 border-b border-slate-800">
                  <div>
                    <p className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      {result.mode}
                    </p>
                    <p className="font-display mt-1 text-xl font-bold uppercase tracking-wide text-slate-100">
                      {result.origin} → {result.destination}
                    </p>
                  </div>
                  <span
                    data-testid="track-status-badge"
                    className="mono inline-flex items-center gap-2 px-3 py-1.5 border border-blue-500/50 text-blue-400 text-[10px] uppercase tracking-[0.2em]"
                  >
                    <span className="w-1.5 h-1.5 bg-blue-400 animate-pulse" />
                    {result.status}
                  </span>
                </div>
                {result.timeline && result.timeline.length > 0 ? (
                  <ol data-testid="track-timeline" className="mt-6 flex flex-col">
                    {result.timeline.map((t, i) => (
                      <li key={i} className="relative flex gap-4 pb-6 last:pb-0">
                        {i < result.timeline.length - 1 && (
                          <span className={`absolute left-[9px] top-6 bottom-0 w-px ${t.state === "done" ? "bg-amber-500/60" : "bg-slate-800"}`} />
                        )}
                        {t.state === "done" && <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0 relative z-10 bg-[#0D1117]" />}
                        {t.state === "active" && <Truck className="w-5 h-5 text-blue-400 shrink-0 relative z-10 bg-[#0D1117] animate-pulse" />}
                        {t.state === "upcoming" && <Circle className="w-5 h-5 text-slate-700 shrink-0 relative z-10 bg-[#0D1117]" />}
                        <div>
                          <p className={`text-sm font-semibold ${t.state === "upcoming" ? "text-slate-600" : "text-slate-200"}`}>
                            {t.label} — {t.place}
                          </p>
                          <p className="mono mt-0.5 text-[11px] uppercase tracking-[0.15em] text-slate-500">
                            {t.time}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p data-testid="track-status-line" className="mt-6 text-sm text-slate-300">
                    Latest status — <span className="text-amber-400 font-semibold">{result.status}</span>
                  </p>
                )}
                {result.source === "preview" && (
                  <p data-testid="track-preview-note" className="mono mt-5 pt-4 border-t border-slate-800 text-[10px] uppercase tracking-[0.15em] text-slate-600">
                    Sample consignment — live Tvarit Control Tower feed pending credentials
                  </p>
                )}
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
